// UX simplification (June 2026) — per-tier sidebar configs.
// Tier 4/5 (COO/CEO) returns NULL → Layout falls back to the existing full sidebar (zero regression).
// Tier 3 (Department Heads) is dept-aware: Finance, HR, and Consulting Directors each see their own subset.
// Feb 2026 update: every tier (2+) gets the new "Approvals" link; Tier 4+ also get the "Recycle Bin" link.

const COMMON_PROFILE = { label: "Settings", to: "/settings", icon: "Gear" };
const APPROVALS_LINK = { label: "Approvals", to: "/approvals", icon: "CheckSquare" };
const RECYCLE_LINK = { label: "Recycle bin", to: "/recycle-bin", icon: "TrashSimple" };

// Tier 1 — Field Operator (5 items)
export const TIER_1_SIDEBAR = [
  { label: "My tasks",       to: "/my-tasks",          icon: "ListChecks" },
  { label: "File sighting",  to: "/reports/sighting/new", icon: "Eye" },
  { label: "Capture",        to: "/capture",           icon: "Camera" },
  { label: "Submit expense", to: "/expenses/new",      icon: "Receipt" },
  { label: "Notifications",  to: "/notifications",     icon: "Bell" },
];

// Tier 2 — Case Manager / Operations (10 items + Approvals)
export const TIER_2_SIDEBAR = [
  { label: "Dashboard",   to: "/dashboard",          icon: "House" },
  { label: "Cases",       to: "/cases",              icon: "FolderOpen" },
  { label: "NFOR",        to: "/cases/nfor",         icon: "FilePlus" },
  { label: "Sightings",   to: "/reports/sighting",   icon: "Camera" },
  { label: "Clients",     to: "/clients",            icon: "Buildings" },
  { label: "Field ops",   to: "/field-ops",          icon: "MapTrifold" },
  { label: "Capture",     to: "/capture",            icon: "Camera" },
  { label: "Escalations", to: "/escalations",        icon: "Warning" },
  { label: "Expenses",    to: "/expenses",           icon: "Receipt" },
  APPROVALS_LINK,
  COMMON_PROFILE,
];

// Tier 3 — Department Heads (role-aware sub-configs)
export const TIER_3_FINANCE = [
  { label: "Dashboard",  to: "/dashboard",  icon: "House" },
  { label: "Cases",      to: "/cases",      icon: "FolderOpen" },
  { label: "Invoices",   to: "/invoices",   icon: "Receipt" },
  { label: "Payables",   to: "/payables",   icon: "CurrencyInr" },
  { label: "Expenses",   to: "/expenses",   icon: "Receipt" },
  { label: "Analytics",  to: "/analytics",  icon: "ChartLine" },
  { label: "Reports",    to: "/reports",    icon: "ChartBar" },
  { label: "Billing",    to: "/billing",    icon: "CreditCard" },
  APPROVALS_LINK,
  COMMON_PROFILE,
];

export const TIER_3_HR = [
  { label: "Dashboard",   to: "/dashboard",      icon: "House" },
  { label: "Team",        to: "/settings/team",  icon: "Users" },
  { label: "Org Chart",   to: "/org",            icon: "Tree" },
  { label: "Analytics",   to: "/analytics",      icon: "ChartLine" },
  { label: "Reports",     to: "/reports",        icon: "ChartBar" },
  APPROVALS_LINK,
  COMMON_PROFILE,
];

export const TIER_3_CONSULTING = [
  { label: "Dashboard",   to: "/dashboard",        icon: "House" },
  { label: "Cases",       to: "/cases",            icon: "FolderOpen" },
  { label: "Clients",     to: "/clients",          icon: "Buildings" },
  { label: "Sightings",   to: "/reports/sighting", icon: "Camera" },
  { label: "Reports",     to: "/reports",          icon: "ChartBar" },
  { label: "Analytics",   to: "/analytics",        icon: "ChartLine" },
  { label: "Map",         to: "/map",              icon: "MapPinLine" },
  APPROVALS_LINK,
  COMMON_PROFILE,
];

// Generic Tier 3 fallback (Quality, Operations heads, etc.)
export const TIER_3_GENERIC = [
  { label: "Dashboard",  to: "/dashboard",        icon: "House" },
  { label: "Cases",      to: "/cases",            icon: "FolderOpen" },
  { label: "Sightings",  to: "/reports/sighting", icon: "Camera" },
  { label: "Reports",    to: "/reports",          icon: "ChartBar" },
  { label: "Analytics",  to: "/analytics",        icon: "ChartLine" },
  APPROVALS_LINK,
  COMMON_PROFILE,
];

/**
 * Returns the sidebar array for the given user, or NULL to indicate
 * the caller should render the existing full sidebar (Tier 4 / 5 = COO/CEO).
 */
export function getSidebarForUser(user) {
  if (!user) return null;
  const tier = user.ux_tier;
  if (tier === 1) return TIER_1_SIDEBAR;
  if (tier === 2) return TIER_2_SIDEBAR;
  if (tier === 3) {
    const dept = (user.department || "").toLowerCase();
    if (dept === "finance") return TIER_3_FINANCE;
    if (dept === "hr") return TIER_3_HR;
    if (dept === "consulting") return TIER_3_CONSULTING;
    return TIER_3_GENERIC;
  }
  // Tier 4 (COO) / 5 (CEO) → null → full sidebar (which adds Approvals + Recycle bin via the extra block)
  return null;
}

/**
 * Extra links rendered on the full sidebar for COO/CEO (Tier 4+).
 * Pulled in by Layout.jsx so we don't have to maintain the full Tier-4 list here.
 */
export const EXEC_EXTRA_LINKS = [
  { label: "Approvals", to: "/approvals", icon: "CheckSquare", min_tier: 4 },
  { label: "Recycle bin", to: "/recycle-bin", icon: "TrashSimple", min_tier: 4 },
  { label: "Custom fields", to: "/settings/custom-fields", icon: "SlidersHorizontal", min_tier: 4 },
  { label: "Feature toggles", to: "/settings/admin/feature-toggles", icon: "Toggle", min_tier: 4 },
  { label: "Departments", to: "/settings/admin/departments", icon: "Buildings", min_tier: 4 },
  { label: "Case Lifecycles", to: "/settings/admin/lifecycles", icon: "TreeStructure", min_tier: 4 },
  { label: "Branding", to: "/settings/admin/branding", icon: "Palette", min_tier: 5 },
  { label: "Invite User", to: "/settings/admin/invite-user", icon: "UserPlus", min_tier: 4 },
  { label: "My Profile", to: "/settings/profile", icon: "User", min_tier: 1 },
  { label: "Command Center", to: "/admin", icon: "ShieldCheck", min_tier: 5 },
  { label: "Audit Trail", to: "/admin/audit-trail", icon: "ChartBar", min_tier: 5 },
  { label: "Drip Campaigns", to: "/stakeholders/drip", icon: "PaperPlaneTilt", min_tier: 3 },
];

/**
 * Mobile bottom-nav for Tier 1 (5 icons). Only rendered below the lg breakpoint.
 */
export const TIER_1_BOTTOM_NAV = TIER_1_SIDEBAR;
